import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  ClipboardCheck, Plus, Upload, Pencil, Trash2, Copy, Eye, Send, Archive,
  Clock, Users, BarChart3, HelpCircle, Zap, BookOpen, MoreVertical,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, StatsCard, EmptyState, SearchInput, ConfirmModal, AppButton, Paginator, ActionMenu, ActionMenuItem, ActionMenuDivider } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./Assessments.scss";

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned" };
const STATUS_LABELS = { draft: "Draft", published: "Published", archived: "Archived" };
const PAGE_SIZE = 20;

const AssessmentsPage = () => {
  const navigate = useNavigate();

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [filterData, setFilterData] = useState({ statuses: {}, modes: {}, totalAssessments: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/assessments/filters");
      setFilterData(data);
    } catch { /* silent */ }
  }, []);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        sort,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(modeFilter && { mode: modeFilter }),
      };
      const { data } = await apiClient.get("/assessments", { params });
      setAssessments(data.assessments || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setToast({ show: true, message: "Failed to load assessments", type: "error" });
    }
    setLoading(false);
  }, [page, search, statusFilter, modeFilter, sort]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchAssessments(); }, [fetchAssessments]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/assessments/${deleteTarget._id}`);
      setToast({ show: true, message: "Assessment deleted", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to delete", type: "error" });
    }
    setDeleteTarget(null);
  };

  const handlePublish = async (id) => {
    try {
      await apiClient.put(`/assessments/${id}/publish`);
      setToast({ show: true, message: "Assessment published!", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to publish", type: "error" });
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await apiClient.post(`/assessments/${id}/duplicate`);
      setToast({ show: true, message: "Assessment duplicated", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to duplicate", type: "error" });
    }
  };

  const handleArchive = async (id) => {
    try {
      await apiClient.put(`/assessments/${id}/archive`);
      setToast({ show: true, message: "Assessment archived", type: "success" });
      fetchAssessments();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to archive", type: "error" });
    }
  };

  return (
    <div className="assessments-page">
      <PageHeader
        icon={ClipboardCheck}
        title="Assessments"
        description={`${filterData.totalAssessments || 0} total assessments`}
        actions={
          <div className="d-flex align-items-center gap-2">
            <AppButton
              variant="outline"
              className="d-flex align-items-center gap-2"
              onClick={() => navigate("/assessments/import")}
            >
              <Upload size={16} /> Import Excel
            </AppButton>
            <AppButton
              variant="default"
              className="d-flex align-items-center gap-2"
              onClick={() => navigate("/assessments/create")}
            >
              <Plus size={16} /> Create Assessment
            </AppButton>
          </div>
        }
      />

      {/* Stats */}
      <div className="assessments-stats-row">
        <StatsCard title="Total" value={filterData.totalAssessments || 0} icon={ClipboardCheck} iconColor="blue" />
        <StatsCard title="Published" value={filterData.statuses?.published || 0} icon={Send} iconColor="green" />
        <StatsCard title="Drafts" value={filterData.statuses?.draft || 0} icon={Pencil} iconColor="orange" />
        <StatsCard title="Archived" value={filterData.statuses?.archived || 0} icon={Archive} iconColor="red" />
      </div>

      {/* Toolbar */}
      <div className="assessments-toolbar">
        <div className="search-wrapper">
          <SearchInput value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search assessments..." />
        </div>
        <div className="filter-wrapper">
          <Form.Select size="sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Form.Select>
        </div>
        <div className="filter-wrapper">
          <Form.Select size="sm" value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}>
            <option value="">All Modes</option>
            {Object.entries(MODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Form.Select>
        </div>
        <div className="filter-wrapper">
          <Form.Select size="sm" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="a-z">A → Z</option>
            <option value="z-a">Z → A</option>
            <option value="most-attempted">Most Attempted</option>
          </Form.Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
      ) : assessments.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck size={40} />}
          title="No assessments yet"
          description={search || statusFilter || modeFilter
            ? "Try adjusting your filters"
            : "Create your first quiz or exam to get started"}
          action={
            !search && !statusFilter ? (
              <AppButton variant="default" onClick={() => navigate("/assessments/create")}>
                <Plus size={16} className="me-1" /> Create Assessment
              </AppButton>
            ) : null
          }
        />
      ) : (
        <>
          <div className="assessment-list">
            {assessments.map((a) => (
              <div className="assessment-card" key={a._id}>
                <div className="assessment-card__header">
                  <div className="flex-grow-1">
                    <div className="assessment-card__badges">
                      <span className={`assessment-card__status assessment-card__status--${a.status}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                      <span className="assessment-card__mode">{MODE_LABELS[a.mode] || a.mode}</span>
                      <span className="assessment-card__selection">
                        {a.selectionMode === "dynamic" ? "Dynamic" : "Manual"}
                      </span>
                    </div>
                    <h3
                      className="assessment-card__title"
                      onClick={() => navigate(`/assessments/${a._id}`)}
                    >
                      {a.title}
                    </h3>
                    {a.description && (
                      <p className="assessment-card__description">{a.description}</p>
                    )}
                  </div>
                  <div className="assessment-card__actions">
                    <ActionMenu trigger={<MoreVertical size={16} />}>
                      <ActionMenuItem onClick={() => navigate(`/assessments/${a._id}`)}>
                        <Eye size={14} /> View
                      </ActionMenuItem>
                      <ActionMenuItem onClick={() => navigate(`/assessments/edit/${a._id}`)}>
                        <Pencil size={14} /> Edit
                      </ActionMenuItem>
                      <ActionMenuItem onClick={() => handleDuplicate(a._id)}>
                        <Copy size={14} /> Duplicate
                      </ActionMenuItem>
                      {a.status === "draft" && (
                        <ActionMenuItem onClick={() => handlePublish(a._id)}>
                          <Send size={14} /> Publish
                        </ActionMenuItem>
                      )}
                      {a.status === "published" && (
                        <ActionMenuItem onClick={() => handleArchive(a._id)}>
                          <Archive size={14} /> Archive
                        </ActionMenuItem>
                      )}
                      {a.totalAttempts === 0 && (
                        <>
                          <ActionMenuDivider />
                          <ActionMenuItem danger onClick={() => setDeleteTarget(a)}>
                            <Trash2 size={14} /> Delete
                          </ActionMenuItem>
                        </>
                      )}
                    </ActionMenu>
                  </div>
                </div>

                <div className="assessment-card__meta">
                  <span className="assessment-card__meta-item">
                    <HelpCircle size={14} /> {a.questionCount || 0} questions
                  </span>
                  {a.timeLimit > 0 && (
                    <span className="assessment-card__meta-item">
                      <Clock size={14} /> {a.timeLimit} min
                    </span>
                  )}
                  {a.passingPercentage > 0 && (
                    <span className="assessment-card__meta-item">
                      Pass: {a.passingPercentage}%
                    </span>
                  )}
                  <span className="assessment-card__meta-item">
                    <BarChart3 size={14} /> {a.totalAttempts} attempts
                  </span>
                  {a.assignedBatches?.length > 0 && (
                    <span className="assessment-card__meta-item">
                      <Users size={14} /> {a.assignedBatches.map(b => b.name || b).join(", ")}
                    </span>
                  )}
                  {a.startDate && (
                    <span className="assessment-card__meta-item">
                      <Clock size={14} /> {new Date(a.startDate).toLocaleDateString()}
                      {a.endDate && ` – ${new Date(a.endDate).toLocaleDateString()}`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Paginator
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          )}
        </>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Assessment"
        message="Are you sure you want to delete this assessment? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default AssessmentsPage;
